/* 20261105 신규: 로그인 전 메뉴를 {landing}/menu.js 로 표시 (디자이너가 메뉴 추가/삭제/순서 변경 가능, 빌드 불필요)
   - 로컬 서버는 클라우드의 파일을 불러오므로(다른 도메인) fetch 대신 <script> 로 읽음 (window.HICLASS_LANDING_MENU)
   - 기존 로그인 전 메뉴(WebHeaderInfoMenu)와 같은 태그/클래스를 사용하므로 header.css 디자인이 그대로 적용됨
   - menu.js 를 읽지 못하거나 형식이 잘못되면 기존 WebHeaderInfoMenu 를 그대로 표시 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { FaQrcode, FaTv } from "react-icons/fa";

import ConstData from "../../../lib/ConstData";
import { changeLandingPage } from "../../../modules/hiclasstv";
import { getLandingBaseUrl, LANDING_CACHE_KEY } from "../../../lib/LandingPath";

import WebHeaderInfoMenu from "./WebHeaderInfoMenu";

const LANDING_MENU_GLOBAL = "HICLASS_LANDING_MENU";    // menu.js 에서 선언하는 전역 변수 이름
const LANDING_MENU_TIMEOUT_MS = 4000;                   // 이 시간 안에 못 읽으면 기존 메뉴 표시

const PAGE_NAME_REGEX = /^[A-Za-z0-9_-]+$/;            // pages/{이름}.html 의 이름 (경로 문자 차단)
const LINK_URL_REGEX = /^(https?:\/\/|\/(?!\/))/i;     // http(s) 주소 또는 같은 서버의 "/..." 경로만 허용
const ICON_PATH_REGEX = /^[A-Za-z0-9_\-./]+$/;          // 20261105 메뉴 아이콘: landing 폴더 기준 상대경로 (예: images/menu/home.svg)
const CLASS_NAME_REGEX = /^[A-Za-z0-9_\- ]+$/;          // 20261105 메뉴 항목에 추가할 클래스 이름

// 20261105 메뉴 아이콘 주소 (http(s) 주소 또는 landing 폴더 기준 상대경로, 그 외는 사용 안 함)
const getMenuIconUrl = (icon) => {
    if (typeof icon !== "string" || icon.trim() === "") {
        return "";
    }

    if (/^https?:\/\//i.test(icon)) {
        return icon;
    }

    if (ICON_PATH_REGEX.test(icon) && icon.indexOf("..") < 0 && icon.charAt(0) !== "/") {
        return getLandingBaseUrl() + icon;
    }

    return "";
};

// 20261105 메뉴 항목 클래스 (nav-item + menu.js 의 className)
const getMenuItemClassName = (item) => {
    return "nav-item" + (typeof item.className === "string" && CLASS_NAME_REGEX.test(item.className) ? " " + item.className.trim() : "");
};

// 20261105 메뉴 이름: "홈" 처럼 문자열 또는 { "ko": "홈", "en": "Home", "ja": "ホーム" } 처럼 언어별 객체
const getMenuLabel = (label, lang) => {
    if (typeof label === "string") {
        return label;
    }

    if (label !== null && typeof label === "object") {
        const candidates = [label[lang], label.ko].concat(Object.keys(label).map(key => label[key]));
        const found = candidates.find(value => typeof value === "string" && value.trim() !== "");
        return found ? found : "";
    }

    return "";
};

// 20261105 menu.js 항목 검증: label 필수 + page 또는 url 중 하나, 로컬 버전이면 hideOnLocal 항목 제외
const normalizeMenuList = (list) => {
    if (!Array.isArray(list)) {
        return null;
    }

    const items = list.filter(item =>
        item !== null && typeof item === "object" &&
        getMenuLabel(item.label, "ko").trim() !== "" &&
        ((typeof item.page === "string" && PAGE_NAME_REGEX.test(item.page)) || (typeof item.url === "string" && LINK_URL_REGEX.test(item.url))) &&
        !(ConstData.IS_LOCAL_VERSION && item.hideOnLocal === true)
    );

    // 본문 페이지가 하나도 없으면 사용할 수 없는 메뉴로 판단
    return items.some(item => item.page) ? items : null;
};

const WebHeaderLandingMenu = ({ isLoginYn, hiclasstvInfo, tossAndHandleSelectedFunc }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    // 20261105 현재 앱 언어 (브라우저 언어 기준, 예: "en-US" → "en")
    const lang = String(i18n.language || "ko").substring(0, 2).toLowerCase();

    const currentLandingPage = useSelector(state => state.hiclasstv.currentLandingPage);

    const [menuStatus, setMenuStatus] = useState("loading");    // loading | ok | fallback
    const [menuList, setMenuList] = useState([]);
    const [isLinkMenuOpened, setIsLinkMenuOpened] = useState(false);

    // 20261105 menu.js 읽기 (<script> 로드 → window.HICLASS_LANDING_MENU), 한 번 읽으면 화면 이동 시 재사용
    useEffect(() => {
        let isCancelled = false;
        let timer = null;

        const finish = (list) => {
            if (isCancelled) return;

            clearTimeout(timer);

            const items = normalizeMenuList(list);

            if (items) {
                setMenuList(items);
                setMenuStatus("ok");
            } else {
                console.log("WebHeaderLandingMenu - invalid or no menu.js => ", list);
                setMenuStatus("fallback");
            }
        };

        if (Array.isArray(window[LANDING_MENU_GLOBAL])) {
            finish(window[LANDING_MENU_GLOBAL]);
        } else {
            const script = document.createElement("script");
            script.src = getLandingBaseUrl() + "menu.js?t=" + LANDING_CACHE_KEY;
            script.async = true;
            script.onload = () => finish(window[LANDING_MENU_GLOBAL]);
            script.onerror = () => finish(null);

            timer = setTimeout(() => finish(window[LANDING_MENU_GLOBAL]), LANDING_MENU_TIMEOUT_MS);
            document.head.appendChild(script);
        }

        return () => {
            isCancelled = true;
            clearTimeout(timer);
        };
    }, []);

    const firstPageName = menuStatus === "ok" ? menuList.find(item => item.page).page : "";
    const isCurrentPageInMenu = menuList.some(item => item.page && item.page === currentLandingPage);
    const activePageName = isCurrentPageInMenu ? currentLandingPage : firstPageName;

    // 20261105 선택된 페이지가 없거나 메뉴에서 빠졌으면 첫 번째 페이지를 선택
    useEffect(() => {
        if (menuStatus === "ok" && !isCurrentPageInMenu && firstPageName !== "") {
            dispatch(changeLandingPage({ pageName: firstPageName }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuStatus, isCurrentPageInMenu, firstPageName]);

    // 20261105 메뉴가 화면보다 넓을 때(모바일) 선택된 메뉴가 보이도록 가로 스크롤 이동
    useEffect(() => {
        if (menuStatus !== "ok" || activePageName === "") return;

        const navbar = document.getElementById("navbar");
        const target = document.getElementById("landing_menu_" + activePageName);

        if (navbar && navbar.parentElement && target && navbar.offsetWidth > navbar.parentElement.offsetWidth) {
            let totalWidth = navbar.offsetWidth;
            let baseWidth = navbar.parentElement.offsetWidth;
            let offset = target.offsetLeft;

            navbar.parentElement.scrollTo(totalWidth - offset < baseWidth ? totalWidth - baseWidth : offset - 12, 0);
        }
    }, [menuStatus, activePageName]);

    const onClickPage = (pageName) => {
        if (isLinkMenuOpened) {
            setIsLinkMenuOpened(false);
        }

        dispatch(changeLandingPage({ pageName }));
    };

    // ---------- 로컬 버전 LINK 메뉴 (기존 WebHeaderInfoMenu 와 동일한 동작) ----------
    const onClickGoToQRLoginPage = () => {
        let origin = window.location.origin;
        window.location = origin + "/main-qrLogin/1001";
    };

    const onClickGoToSmartTVLoginPage = (mode) => {
        let regEx = new RegExp("https://", "gi");
        let origin = window.location.origin;
        let port = window.location.port;
        let host = origin.replace(regEx, "http://");
        let location = host;

        if (port !== "3000") {
            location += ":8080";
        }

        if (mode === "web-smarttv") {
            location += "/smarttv-login/1001";
        } else {
            location += "/group-smarttv-login/1001";
        }

        if (location !== "") {
            console.log(location);
            window.location = location;
        }
    };

    if (menuStatus === "fallback") {
        return (
            <WebHeaderInfoMenu
                isMobile={true}
                isLoginYn={isLoginYn}
                isInfoPage={true}
                hiclasstvInfo={hiclasstvInfo}
                tossAndHandleSelectedFunc={tossAndHandleSelectedFunc}
            />
        );
    }

    if (menuStatus === "loading") {
        return null;
    }

    return (
        <>
            {
                menuList.map((item, index) =>
                    item.page ?
                        <li className={getMenuItemClassName(item)} id={"landing_menu_" + item.page} key={"landing_menu_" + index}>
                            <span className={item.page === activePageName ? "hiclasstv-deepdarkblue-text" : "white-text"} onClick={() => onClickPage(item.page)}>
                                {getMenuIconUrl(item.icon) !== "" && <img className="lp-menu-icon" src={getMenuIconUrl(item.icon)} alt="" />}
                                {getMenuLabel(item.label, lang)}
                            </span>
                        </li> :
                        <li className={getMenuItemClassName(item)} key={"landing_menu_" + index}>
                            <a href={item.url} target="_blank" rel="noopener noreferrer">
                                <span className="white-text">
                                    {getMenuIconUrl(item.icon) !== "" && <img className="lp-menu-icon" src={getMenuIconUrl(item.icon)} alt="" />}
                                    {getMenuLabel(item.label, lang)}
                                </span>
                            </a>
                        </li>
                )
            }
            {
                ConstData.IS_LOCAL_VERSION && isLoginYn === "N" &&
                <li id="tool_link" className="nav-item flex-column" onClick={() => setIsLinkMenuOpened(!isLinkMenuOpened)}>
                    <span className="white-text">{t("LINK", { ns: "hiclasstv" })}</span>
                </li>
            }
            {
                ConstData.IS_LOCAL_VERSION && isLoginYn === "N" && isLinkMenuOpened &&
                <div className="d-flex menu-category">
                    <li className="nav-item flex-column" onClick={onClickGoToQRLoginPage}>
                        <FaQrcode className="font-md plus white-text" />
                        <span className="font-xxsm white-text">{t("QR", { ns: "hiclasstv" })}</span>
                    </li>
                    <li className="nav-item flex-column" onClick={() => onClickGoToSmartTVLoginPage("web-smarttv")}>
                        <FaTv className="font-md plus white-text" />
                        <span className="font-xxsm white-text">{t("SmartTV", { ns: "hiclasstv" })}</span>
                    </li>
                    <li className="nav-item flex-column" onClick={() => onClickGoToSmartTVLoginPage("web-group-smarttv")}>
                        <FaTv className="font-md plus white-text" />
                        <span className="font-xxsm white-text">{t("GroupSmartTV", { ns: "hiclasstv" })}</span>
                    </li>
                </div>
            }
        </>
    );
};

export default WebHeaderLandingMenu;
